'use client';

import React, { useState, useEffect } from 'react';

export default function IOSInstallPrompt() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only show on iOS Safari (not in standalone mode)
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isInStandaloneMode =
      ('standalone' in window.navigator && (window.navigator as Navigator & { standalone?: boolean }).standalone === true) ||
      window.matchMedia('(display-mode: standalone)').matches;
    const wasDismissed = localStorage.getItem('ios-install-dismissed') === 'true';

    if (isIOS && !isInStandaloneMode && !wasDismissed) {
      // Delay slightly so it doesn't pop up immediately on page load
      const timer = setTimeout(() => setShow(true), 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    setShow(false);
    localStorage.setItem('ios-install-dismissed', 'true');
  };

  if (!show || dismissed) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Checkout Genie"
      className="fixed bottom-4 left-4 right-4 z-[100] mx-auto max-w-sm"
    >
      <div
        className="rounded-2xl p-4 shadow-2xl border border-white/10"
        style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #0f0f1a 100%)' }}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          className="absolute top-3 right-3 text-white/50 hover:text-white/90 transition-colors text-lg leading-none"
        >
          ✕
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-3 pr-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
          >
            <span className="text-lg">🧞</span>
          </div>
          <div>
            <p className="text-white font-semibold text-sm leading-tight">Add to Home Screen</p>
            <p className="text-white/60 text-xs">Install Checkout Genie for quick access</p>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-2 mb-3">
          <div className="flex items-start gap-2">
            <span className="text-white/40 text-xs mt-0.5 w-4 flex-shrink-0">1.</span>
            <p className="text-white/70 text-xs">
              Tap the{' '}
              <span className="inline-flex items-center gap-0.5 text-white font-medium">
                Share
                <svg className="w-3 h-3 inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>{' '}
              button at the bottom of Safari
            </p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-white/40 text-xs mt-0.5 w-4 flex-shrink-0">2.</span>
            <p className="text-white/70 text-xs">
              Scroll down and tap{' '}
              <span className="text-white font-medium">"Add to Home Screen"</span>
            </p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-white/40 text-xs mt-0.5 w-4 flex-shrink-0">3.</span>
            <p className="text-white/70 text-xs">
              Tap <span className="text-white font-medium">"Add"</span> — that's it!
            </p>
          </div>
        </div>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="w-full text-center text-white/40 text-xs hover:text-white/60 transition-colors py-1"
        >
          Maybe later
        </button>
      </div>

      {/* Arrow pointing down toward Safari toolbar */}
      <div className="flex justify-center mt-1">
        <div
          className="w-4 h-4 rotate-45 border-r border-b border-white/10"
          style={{ background: '#0f0f1a' }}
        />
      </div>
    </div>
  );
}
